import { useEffect, useRef, useState } from 'react';
import { projectEndTicks } from '@/domain/selectors';
import { keyboardPitch } from '@/engine/MidiInput';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';
import { TopBar } from './TopBar';
import { TrackHeaders } from './TrackHeaders';
import { Timeline } from './timeline/Timeline';
import { Mixer } from './panels/Mixer';
import { Inspector } from './panels/Inspector';
import { Spectrum } from './panels/Spectrum';
import { TempoLane } from './panels/TempoLane';
import { PianoRoll } from './panels/PianoRoll';
import { DrumSequencer } from './panels/DrumSequencer';
import { StatusBar } from './StatusBar';
import { Modals, promptImportAudio, promptImportMidi } from './Modals';

export function App(): JSX.Element {
  const ready = useDaw((s) => s.ready);
  const recovery = useDaw((s) => s.recoveryOffer);
  const toasts = useDaw((s) => s.toasts);
  const [panel, setPanel] = useState<'mixer' | 'editor' | 'inspector' | 'spectrum' | 'tempo' | 'none'>('mixer');
  const editorKind = useDaw((s) => {
    const clip = s.selection.clipIds.length === 1 ? s.project.clips[s.selection.clipIds[0]] : null;
    if (!clip || clip.kind !== 'midi') return 'pianoRoll';
    // A drum kit gets the step grid; anything else gets the piano roll.
    return s.project.tracks[clip.trackId]?.instrument?.type === 'drumMachine' ? 'sequencer' : 'pianoRoll';
  });

  // Selecting a MIDI clip opens the editor, because that is invariably why
  // someone selected it.
  const editedClipId = useDaw((s) => {
    const clip = s.selection.clipIds.length === 1 ? s.project.clips[s.selection.clipIds[0]] : null;
    return clip?.kind === 'midi' ? clip.id : null;
  });
  useEffect(() => {
    if (editedClipId) setPanel((current) => (current === 'none' ? current : 'editor'));
  }, [editedClipId]);
  const [panelHeight, setPanelHeight] = useState(250);
  const resizeRef = useRef<{ startY: number; startHeight: number } | null>(null);

  useEffect(() => {
    void useDaw.getState().boot();
  }, []);

  useKeyboardShortcuts();
  useMidiInput();
  useMusicalTyping();
  useUnloadGuard();

  if (!ready) {
    return (
      <div className="app" style={{ display: 'grid', placeItems: 'center' }}>
        <div style={{ color: 'var(--text-2)' }}>Opening your local workspace…</div>
      </div>
    );
  }

  return (
    <div className="app">
      <TopBar />

      <div className="app-body">
        <div className="workspace">
          {recovery && (
            <div className="banner" role="alert">
              <span style={{ flex: 1 }}>
                Unsaved work from {new Date(recovery.snapshot.savedAt).toLocaleString()} was found for “
                {recovery.snapshot.projectName}”. Your last saved version has not been touched.
              </span>
              <button className="btn btn--primary" onClick={() => void useDaw.getState().acceptRecovery()}>
                Recover it
              </button>
              <button className="btn" onClick={() => void useDaw.getState().discardRecovery()}>
                Discard
              </button>
            </div>
          )}

          <div className="arrangement">
            <TrackHeaders />
            <Timeline />
          </div>

          {panel !== 'none' && (
            <div
              className="panel"
              style={{ height: panelHeight }}
              onPointerMove={(e) => {
                const state = resizeRef.current;
                if (!state) return;
                setPanelHeight(Math.min(560, Math.max(120, state.startHeight - (e.clientY - state.startY))));
              }}
              onPointerUp={() => {
                resizeRef.current = null;
              }}
            >
              <div
                style={{ height: 4, cursor: 'ns-resize', marginTop: -4 }}
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  resizeRef.current = { startY: e.clientY, startHeight: panelHeight };
                }}
                role="separator"
                aria-label="Resize the bottom panel"
              />
              <div className="panel__tabs">
                <button className={`tab ${panel === 'mixer' ? 'tab--active' : ''}`} onClick={() => setPanel('mixer')}>
                  Mixer
                </button>
                <button className={`tab ${panel === 'editor' ? 'tab--active' : ''}`} onClick={() => setPanel('editor')}>
                  {editorKind === 'sequencer' ? 'Sequencer' : 'Piano Roll'}
                </button>
                <button className={`tab ${panel === 'inspector' ? 'tab--active' : ''}`} onClick={() => setPanel('inspector')}>
                  Inspector
                </button>
                <button className={`tab ${panel === 'spectrum' ? 'tab--active' : ''}`} onClick={() => setPanel('spectrum')}>
                  Spectrum
                </button>
                <button className={`tab ${panel === 'tempo' ? 'tab--active' : ''}`} onClick={() => setPanel('tempo')}>
                  Tempo
                </button>
                <div style={{ flex: 1 }} />
                <button className="btn btn--ghost" onClick={() => void promptImportAudio()}>
                  Import audio…
                </button>
                <button className="btn btn--ghost" onClick={() => void promptImportMidi()}>
                  Import MIDI…
                </button>
                <button className="btn btn--ghost" onClick={() => useDaw.getState().setModal('shortcuts')}>
                  Shortcuts
                </button>
                <button className="btn btn--ghost" onClick={() => setPanel('none')} aria-label="Hide the bottom panel">
                  ▾
                </button>
              </div>
              <div className="panel__body" style={panel === 'editor' ? { display: 'flex', flexDirection: 'column' } : undefined}>
                {panel === 'mixer' && <Mixer />}
                {panel === 'editor' && (editorKind === 'sequencer' ? <DrumSequencer /> : <PianoRoll />)}
                {panel === 'inspector' && <Inspector />}
                {panel === 'spectrum' && <Spectrum />}
                {panel === 'tempo' && <TempoLane />}
              </div>
            </div>
          )}

          {panel === 'none' && (
            <div className="panel">
              <div className="panel__tabs">
                <button className="btn btn--ghost" onClick={() => setPanel('mixer')}>
                  ▴ Show mixer
                </button>
                <div style={{ flex: 1 }} />
                <button className="btn btn--ghost" onClick={() => void promptImportAudio()}>
                  Import audio…
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <StatusBar />
      <Modals />

      <div className="toasts">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.kind}`} role="status">
            <div style={{ flex: 1 }}>
              <div className="toast__title">{toast.title}</div>
              {toast.detail && <div className="toast__detail">{toast.detail}</div>}
            </div>
            <button className="btn btn--ghost" onClick={() => useDaw.getState().dismissToast(toast.id)} aria-label="Dismiss">
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Global shortcuts. They are deliberately inert while a text field has focus,
 * so typing a track name never triggers the transport — an error that is easy
 * to make and very annoying to live with.
 */
function useKeyboardShortcuts(): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable === true;
      const store = useDaw.getState();
      const mod = event.metaKey || event.ctrlKey;

      if (mod) {
        switch (event.key.toLowerCase()) {
          case 'z':
            event.preventDefault();
            if (event.shiftKey) store.redo();
            else store.undo();
            return;
          case 'y':
            event.preventDefault();
            store.redo();
            return;
          case 's':
            event.preventDefault();
            void store.saveProject();
            return;
          case 'o':
            event.preventDefault();
            store.setModal('projects');
            return;
          case 'e':
            event.preventDefault();
            store.setModal('export');
            return;
          case 'f':
            if (typing) return;
            event.preventDefault();
            store.crossfadeSelection();
            return;
          case 'i':
            if (typing) return;
            event.preventDefault();
            store.applyRangeEdit('insert', event.shiftKey);
            return;
          case 'c':
            if (typing) return;
            event.preventDefault();
            store.copySelection();
            return;
          case 'x':
            if (typing) return;
            event.preventDefault();
            store.cutSelection();
            return;
          case 'v':
            if (typing) return;
            event.preventDefault();
            store.pasteClipboard();
            return;
          case 'd':
            if (typing) return;
            event.preventDefault();
            store.duplicateSelection();
            return;
          case 'a':
            if (typing) return;
            event.preventDefault();
            store.setSelection({ clipIds: Object.keys(store.project.clips) });
            return;
          default:
            return;
        }
      }

      if (typing) return;

      switch (event.key) {
        case ' ':
          event.preventDefault();
          store.togglePlay();
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          // Shift closes the gap; plain Delete leaves it. Both are wanted often
          // enough that neither should need a menu.
          if (event.shiftKey && store.selection.range) store.applyRangeEdit('spliceOut', false);
          else store.deleteSelection();
          break;
        case 'Home':
          event.preventDefault();
          store.seekTicks(0);
          break;
        case 'End':
          event.preventDefault();
          store.seekTicks(projectEndTicks(store.project));
          break;
        case 'Escape':
          store.setModal('none');
          store.setSelection({ clipIds: [] });
          store.setRange(null);
          break;
        default:
          break;
      }

      switch (event.key.toLowerCase()) {
        case 'r':
          event.preventDefault();
          void store.toggleRecording();
          break;
        case 's':
          event.preventDefault();
          store.splitSelectionAtPlayhead();
          break;
        case 'l':
          store.toggleLoop();
          break;
        case 'k':
          store.toggleMetronome();
          break;
        case 'm':
          if (store.selection.trackId) {
            const track = store.project.tracks[store.selection.trackId];
            if (track) store.dispatch({ type: 'track.setMute', params: { trackId: track.id, mute: !track.mute } });
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}

/** Routes incoming MIDI to the armed instrument track. */
function useMidiInput(): void {
  const midiStatus = useDaw((s) => s.midiStatus);

  useEffect(() => {
    if (midiStatus !== 'open') return;
    const store = useDaw.getState();
    return getServices().midi.subscribe((event) => {
      if (event.type === 'noteOn') store.playLiveNote(event.pitch, event.velocity);
      else if (event.type === 'noteOff') store.stopLiveNote(event.pitch);
      else if (event.type === 'sustain') store.setSustainPedal(event.down);
      else if (event.type === 'pitchBend') store.bendLive(event.amount);
      else if (event.type === 'modWheel') store.modWheelLive(event.amount);
      else {
        // An all-notes-off from a controller must reach every held note, not
        // just the ones this session knows about — and not be held by a pedal
        // that is still down, so the pedal is lifted first.
        store.setSustainPedal(false);
        for (let pitch = 0; pitch <= 127; pitch++) store.stopLiveNote(pitch);
      }
    });
  }, [midiStatus]);
}

/**
 * The computer keyboard as an instrument.
 *
 * Active only while an instrument track is armed, so the letter keys keep their
 * normal shortcuts the rest of the time — a DAW that swallows `S` because a
 * synth exists somewhere in the project is a DAW people fight with. Auto-repeat
 * is filtered, or holding a key would retrigger the note continuously.
 */
function useMusicalTyping(): void {
  useEffect(() => {
    const held = new Set<string>();

    const isTyping = (target: EventTarget | null): boolean =>
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      (target as HTMLElement | null)?.isContentEditable === true;

    const armed = (): boolean => {
      const project = useDaw.getState().project;
      return project.trackOrder.some(
        (id) => project.tracks[id]?.armed && project.tracks[id]?.kind === 'instrument',
      );
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target) || !armed()) return;
      const store = useDaw.getState();

      if (event.key === 'z' || event.key === 'x') {
        event.preventDefault();
        store.setKeyboardOctave(store.keyboardOctave + (event.key === 'x' ? 1 : -1));
        return;
      }

      const pitch = keyboardPitch(event.key, store.keyboardOctave);
      if (pitch === null || held.has(event.key)) return;
      event.preventDefault();
      held.add(event.key);
      store.playLiveNote(pitch, 100);
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (!held.delete(event.key)) return;
      const store = useDaw.getState();
      const pitch = keyboardPitch(event.key, store.keyboardOctave);
      if (pitch !== null) store.stopLiveNote(pitch);
    };

    // Losing focus mid-note would otherwise leave it sounding for ever.
    const releaseAll = (): void => {
      const store = useDaw.getState();
      for (const key of held) {
        const pitch = keyboardPitch(key, store.keyboardOctave);
        if (pitch !== null) store.stopLiveNote(pitch);
      }
      held.clear();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', releaseAll);
    return () => {
      releaseAll();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseAll);
    };
  }, []);
}

/**
 * Writes a final recovery snapshot as the tab closes. `visibilitychange` is
 * used rather than `beforeunload` because it is the only event mobile browsers
 * reliably fire before discarding a tab.
 */
function useUnloadGuard(): void {
  useEffect(() => {
    const services = getServices();

    const flush = (): void => {
      const state = useDaw.getState();
      if (state.dirty) {
        services.autosave.notifyChanged(state.project);
        void services.autosave.flush();
      }
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') flush();
    };

    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      flush();
      if (useDaw.getState().dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', flush);
    };
  }, []);
}
